import axios from './axiosInstance';
import { DrugIdentification } from '../common/models/types';

// Admin 목록용 확장 타입 (User 정보 포함)
export interface DrugIdentificationWithUser extends DrugIdentification {
  user?: {
    id: string;
    email: string;
    name: string;
    phone: string;
  };
}

// 목록 조회 요청 파라미터
export interface DrugIdentificationListParams {
  userId?: string;
  startDate?: string;
  endDate?: string;
  withUser?: boolean;
  withResults?: boolean;
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
}

// 목록 조회 응답
export interface DrugIdentificationListResponse {
  error: number;
  data: DrugIdentificationWithUser[];
  meta: {
    totalItems: number;
    count: number;
    hasMore: boolean;
    offset: number;
    limit: number;
  };
}

/**
 * Admin용 의약품 식별 목록 조회
 */
export const fetchDrugIdentificationList = async (
  params: DrugIdentificationListParams
): Promise<DrugIdentificationListResponse> => {
  const response = await axios.get('/api/v1/drugs/identify/admin/list', { params });
  return response.data;
};

/**
 * 의약품 식별 상세 조회
 */
export const fetchDrugIdentificationById = async (
  id: number
): Promise<{ error: number; data: DrugIdentificationWithUser }> => {
  const response = await axios.get(`/api/v1/drugs/identify/${id}`);
  return response.data;
};

/**
 * 의약품 식별 이미지 S3 경로 상수
 * drugImageUtils.ts의 DRUG_IMAGE_BASE_URL과 동일한 base URL 사용
 */
export const DRUG_IDENTIFICATION_IMAGE_BASE_URL =
  'https://s3.ap-northeast-2.amazonaws.com/storage.dev.ymyd.onjourney.co.kr/phai/drug-identifications';

/**
 * S3 이미지 URL 생성
 * @param id 식별 요청 ID
 * @param index 이미지 인덱스 (0 또는 1)
 */
export const getDrugIdentificationImageUrl = (id: number, index: number): string => {
  return `${DRUG_IDENTIFICATION_IMAGE_BASE_URL}/${id}-${index}.jpg`;
};

/**
 * 의약품 식별 요청 삭제 (Soft Delete)
 */
export const deleteDrugIdentification = async (
  id: number
): Promise<{ error: number; data: object }> => {
  const response = await axios.delete(`/api/v1/drugs/identify/${id}`);
  return response.data;
};

/**
 * 검색 조건 수정 요청 타입
 */
export interface DrugIdentificationUpdateParams {
  identifyText1?: string | string[] | null;
  identifyText2?: string | string[] | null;
  formulation?: string[];
  shape?: string[];
  color?: string[];
  line?: string[];
  limit?: number;
}

/**
 * 의약품 검색 조건 수정 및 재검색
 */
export const updateDrugIdentificationCondition = async (
  id: number,
  params: DrugIdentificationUpdateParams
): Promise<{ error: number; data: DrugIdentificationWithUser }> => {
  const response = await axios.patch(`/api/v1/drugs/identify/${id}`, params);
  return response.data;
};

/**
 * 의약품 검색 요청 파라미터 (ID 없이 검색만 수행)
 */
export interface DrugSearchParams {
  id?: number;
  identifyText1?: string | string[] | null;
  identifyText2?: string | string[] | null;
  formulation?: string[];
  shape?: string[];
  color?: string[];
  line?: string[];
  limit?: number;
}

/**
 * 의약품 검색 결과 항목
 */
export interface DrugSearchResultItem {
  itemSeq: string;
  itemName: string;
  entpName: string;
  formulation: string;
  shape: string;
  colorClass1: string;
  colorClass2?: string;
  itemImage: string;
  printFront: string;
  printBack: string;
  lineFront?: string;
  lineBack?: string;
  score?: number;
}

/**
 * 의약품 검색 응답
 */
export interface DrugSearchResponse {
  error: number;
  data: {
    id?: number;
    conditions: {
      identifyText1?: string | string[];
      identifyText2?: string | string[];
      formulation?: string[];
      shape?: string[];
      color?: string[];
      line?: string[];
    };
    results: DrugSearchResultItem[];
  };
}

/**
 * 의약품 검색 (ID 없이 조건만으로 검색)
 */
export const searchDrugs = async (
  params: DrugSearchParams
): Promise<DrugSearchResponse> => {
  const response = await axios.post('/api/v1/drugs/identify/search', params);
  return response.data;
};

/**
 * 의약품 식별 환자 연결 및 메모 업데이트
 */
export const updateDrugIdentificationPatient = async (
  id: number,
  data: { patientId?: number | null; note?: string | null },
): Promise<{ error: number; data: object }> => {
  const response = await axios.patch(`/api/v1/drugs/identify/${id}/patient`, data);
  return response.data;
};

/**
 * 사용자용 식별 목록 조회 파라미터
 */
export interface UserDrugIdentificationParams {
  patientId?: number;   // 환자 ID 필터
  status?: string;      // 'selected'로 고정
  limit?: number;
  offset?: number;
  startDate?: string;   // 시작 날짜 (YYYY-MM-DD)
  endDate?: string;     // 종료 날짜 (YYYY-MM-DD)
}

/**
 * 사용자용 응답 타입 (User 정보 없이 DrugIdentification만)
 */
export interface UserDrugIdentificationListResponse {
  error: number;
  data: DrugIdentification[];  // WithUser가 아닌 기본 타입
  meta: {
    totalItems: number;
    count: number;
    hasMore: boolean;
    offset: number;
    limit: number;
  };
}

/**
 * 사용자용 의약품 식별 목록 조회
 */
export const fetchUserDrugIdentificationList = async (
  params: UserDrugIdentificationParams
): Promise<UserDrugIdentificationListResponse> => {
  const response = await axios.get('/api/v1/drugs/identify/list', { params });
  return response.data;
};
